import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { Result } from '@obraplan/core/domain/shared/result';
import { publishChange } from './revalidation';
import { AppError } from '@obraplan/core/application/errors/app-error';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';

export interface CommandState {
  readonly isPending: boolean;
  readonly error: AppError | undefined;
}

export interface UseCommandOptions<T> {
  readonly successMessage?: string | ((value: T) => string);
  readonly onSuccess?: (value: T) => void | Promise<void>;
  /** Silencia o aviso automático — use quando a tela já mostra o erro no formulário. */
  readonly silent?: boolean;
}

/**
 * Executa um caso de uso a partir da interface.
 *
 * Cuida de estado pendente, erro tipado, notificação — e do **aviso de que algo mudou**.
 *
 * Esse último passou a existir na Fase 12. Enquanto os dados moravam no navegador, o
 * `useLiveQuery` do Dexie via a escrita acontecer e atualizava as telas sozinho; com os
 * dados no servidor não há o que observar, e alguém precisa dizer. É aqui, e num lugar só:
 * este é o funil por onde toda escrita da interface passa, e concentrar o aviso nele é o
 * que evita que cada tela tenha de lembrar de avisar — lembrar é o que falha (RN-152).
 *
 * Erro inesperado é convertido em `AppError` genérico — a tela nunca recebe pilha.
 */
export function useCommand<TInput extends unknown[], TValue>(
  command: (...args: TInput) => Promise<Result<TValue, AppError>>,
  options: UseCommandOptions<TValue> = {},
): [(...args: TInput) => Promise<Result<TValue, AppError> | undefined>, CommandState] {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<AppError | undefined>(undefined);

  const run = useCallback(
    async (...args: TInput) => {
      setIsPending(true);
      setError(undefined);
      try {
        const result = await command(...args);
        if (result.ok) {
          if (options.successMessage !== undefined) {
            const message =
              typeof options.successMessage === 'function'
                ? options.successMessage(result.value)
                : options.successMessage;
            toast.success(message);
          }
          // Antes do `onSuccess`: o que a tela faz em seguida — fechar o diálogo, navegar —
          // costuma depender de a lista já estar sendo rebuscada.
          publishChange();
          await options.onSuccess?.(result.value);
        } else {
          setError(result.error);
          if (options.silent !== true) {
            toast.error(messageForCode(result.error.code, result.error.message));
          }
        }
        return result;
      } catch (cause) {
        const appError = AppError.isAppError(cause)
          ? cause
          : new AppError('unexpected', 'SYSTEM.UNEXPECTED', messageForCode('SYSTEM.UNEXPECTED'), {
              cause,
            });
        // A pilha fica no console para diagnóstico; a tela recebe só a mensagem.
        console.error('[obraplan] falha inesperada', cause);
        setError(appError);
        if (options.silent !== true) toast.error(appError.message);
        return undefined;
      } finally {
        setIsPending(false);
      }
    },
    [command, options],
  );

  return [run, { isPending, error }];
}
